// The chart's lenses (CHART-LENSES; docs/design/2026-10-21-chart-lenses.md; CHART-KB adds
// Execution, Keystones and Completeness, docs/design/2026-10-21-chart-kb.md), the pure client
// part: the tabs across the top of the org chart — Structure (the floor), Performance,
// Execution, Responsibilities, Tasks, Finance, Keystones, Overall and Completeness — what each
// reads from `GET org-chart/dimensions`, the score and band of a seat under a lens (the maths is
// `@companyos/domain-work/org`, which the API uses for Overall and Completeness too), the branch
// roll-up a manager's card shows, which lens the viewer may open (the server leaves a hidden
// dimension out of every seat; a lens hidden on every seat is locked), the viewer's remembered
// lens and the `?lens=` URL. KPI Beast's mode bar was the reference ("I could switch between
// modes … and instantly understand the org"); nothing of its shape is copied: every lens is an
// existing CompanyOS entity.
import {
  bandOf,
  branchRollup,
  completenessBand,
  executionScore,
  financeScore,
  keystonesScore,
  LENSES,
  type Lens,
  type LensBand,
  type LensScore,
  type LensThresholds,
  performanceScore,
  responsibilitiesScore,
  SCORED_LENSES,
  type ScoredLens,
  tasksScore,
} from "@companyos/domain-work/org";
import type { OrgChartDimensions } from "../../api/types";
import type { Dimension, GatedDimension, Level, SeatDimensions } from "./chart-dimensions";
import { type ChartSeat, type SeatNode, underOf } from "./chart-model";

export type { Lens, LensBand, LensScore, LensThresholds, ScoredLens };
export { LENSES, SCORED_LENSES };

export type LensConfig = OrgChartDimensions["lenses"];

export const LENS_LABELS: Record<Lens, string> = {
  structure: "Structure",
  performance: "Performance",
  execution: "Execution",
  responsibilities: "Responsibilities",
  tasks: "Tasks",
  finance: "Finance",
  keystones: "Priorities",
  overall: "Overall",
  completeness: "Completeness",
};

/** What each lens reads, in one line (the legend and Setup › Chart say it). */
export const LENS_HINTS: Record<Lens, string> = {
  structure: "Who holds each seat, its title, reporting line, teams and responsibilities",
  performance: "The seat scorecard's band and KPI score for the focused week",
  execution: "The seat's goal score: its weighted target attainment for the focused week",
  responsibilities: "Coverage: the share of each seat's responsibilities with measurables",
  tasks: "On-time share of the seat's open work: 100 minus the overdue percentage",
  finance: "The seat's budget used this month (actual over budget)",
  keystones: "The quarter's priorities on the seat and the share of them on track",
  overall: "The weighted combination of the scored lenses",
  completeness: "Seat setup: the share of the required items in place on the seat",
};

/** The unit a lens's number wears on a card. */
export const LENS_UNITS: Record<Lens, string> = {
  structure: "",
  performance: "",
  execution: "",
  responsibilities: "%",
  tasks: "",
  finance: "%",
  keystones: "%",
  overall: "",
  completeness: "%",
};

/**
 * The gated dimension a lens is read through: its audience is that dimension's (no parallel
 * ACL). Execution reads the scorecard, so it follows Performance; Completeness has a fixed
 * audience (the holder, the line above and managers) and no key.
 */
export const LENS_GATE: Partial<Record<Lens, GatedDimension>> = {
  performance: "kpi",
  execution: "kpi",
  tasks: "load",
  finance: "cost",
  keystones: "keystones",
  overall: "overall",
};

/** The dimension a lens needs the server to have answered. */
const LENS_DIMENSION: Partial<Record<Lens, Dimension>> = {
  responsibilities: "responsibilities",
  performance: "kpi",
  execution: "kpi",
  tasks: "load",
  finance: "cost",
};

/** The lenses with a score of their own on a card (every one but the floor). */
export const SCORING_LENSES: readonly Lens[] = LENSES.filter((l) => l !== "structure");

export const isLens = (v: unknown): v is Lens =>
  typeof v === "string" && (LENSES as readonly string[]).includes(v);

/** The lenses the company serves: Performance and Execution only with the `kpi` add-on. */
export function servedLenses(served: readonly Dimension[]): Lens[] {
  return LENSES.filter((l) => {
    const d = LENS_DIMENSION[l];
    return !d || served.includes(d);
  });
}

/**
 * Whether the server answered the lens's dimension on the seat (a dimension the viewer may not
 * see is absent, not nulled: the floor and Overall are "visible" when present).
 */
export function lensVisible(lens: Lens, d: SeatDimensions | undefined): boolean {
  if (!d) return false;
  switch (lens) {
    case "structure":
    case "responsibilities":
      return d.responsibilities !== undefined;
    case "performance":
    case "execution":
      return d.kpi !== undefined;
    case "tasks":
      return d.load !== undefined;
    case "finance":
      return d.cost !== undefined;
    case "keystones":
      return d.keystones !== undefined;
    case "overall":
      return d.overall !== undefined;
    case "completeness":
      return d.completeness !== undefined;
  }
}

/**
 * Whether the seat carries anything under the lens: a card with nothing in the lens (no open
 * tasks, no priorities this quarter, no scorecard) is drawn dimmed, the way KPI Beast greys a
 * seat a space does not apply to. A hidden lens is not "empty": it says who sees it instead.
 */
export function lensCarried(lens: Lens, d: SeatDimensions | undefined): boolean {
  if (!d || !lensVisible(lens, d)) return false;
  switch (lens) {
    case "structure":
      return true;
    case "responsibilities":
      return (d.responsibilities?.count ?? 0) > 0;
    case "performance":
    case "execution":
      return d.kpi !== null && d.kpi !== undefined;
    case "tasks":
      return (d.load?.open ?? 0) > 0;
    case "finance":
      return d.cost !== null && d.cost !== undefined;
    case "keystones":
      return (d.keystones?.count ?? 0) > 0;
    case "overall":
      return (d.overall?.scored ?? 0) > 0;
    case "completeness":
      return d.completeness !== undefined;
  }
}

/** A lens hidden on every seat of the chart: locked for this viewer (shown, not openable). */
export function lensLocked(lens: Lens, seats: readonly SeatDimensions[]): boolean {
  if (lens === "structure" || lens === "responsibilities") return false;
  return seats.length > 0 && !seats.some((d) => lensVisible(lens, d));
}

/** The seat's score under a lens, from the server's answer and the company's steps. */
export function lensScoreOf(
  lens: Lens,
  seat: Pick<ChartSeat, "holders">,
  d: SeatDimensions | undefined,
  t: LensThresholds,
): LensScore {
  switch (lens) {
    case "structure":
      return { score: null, band: "none" };
    case "responsibilities":
      return responsibilitiesScore(
        d?.responsibilities,
        seat.holders.length > 0,
        t.responsibilities,
      );
    case "performance":
      return performanceScore(d?.kpi);
    case "execution":
      return executionScore(d?.kpi, t.execution);
    case "tasks":
      return tasksScore(d?.load, t.tasks);
    case "finance":
      return financeScore(d?.cost, t.finance);
    case "keystones":
      return keystonesScore(d?.keystones, t.keystones);
    case "overall":
      return d?.overall
        ? {
            score: d.overall.score,
            band: d.overall.band,
            ...(d.overall.score === null
              ? {
                  note:
                    d.overall.scored > 0
                      ? `Too few lenses scored (${d.overall.scored} of ${d.overall.total})`
                      : "Awaiting data",
                }
              : {}),
          }
        : { score: null, band: "none", note: "Awaiting data" };
    case "completeness":
      return d?.completeness
        ? { score: d.completeness.score, band: completenessBand(d.completeness.score) }
        : { score: null, band: "none", note: "Shown to the seat's line" };
  }
}

export type Rollup = { score: number | null; band: LensBand; seats: number };

/**
 * A branch's roll-up under a lens: the mean of the scores present on the manager's seat and
 * every seat under it (the viewer's permitted ones; hidden seats count nothing), with the
 * number of seats that had one. Null for the Structure lens.
 */
export function branchOf(
  lens: Lens,
  node: SeatNode,
  scoreOf: (seat: ChartSeat) => LensScore,
  t: LensThresholds,
): Rollup | null {
  if (lens === "structure" || node.children.length + node.staff.length === 0) return null;
  const scores: (number | null)[] = [];
  // CHART-STAFF: staff seats are under the manager too, so they count in the branch.
  for (const n of [node, ...underOf(node)]) {
    const s = scoreOf(n.seat);
    scores.push(s.band === "none" ? null : s.score);
  }
  const present = scores.filter((s) => s !== null).length;
  const score = branchRollup(scores);
  const band: LensBand =
    score === null
      ? "none"
      : lens === "completeness"
        ? completenessBand(score)
        : bandOf(lens, score, t);
  return { score, band, seats: present };
}

/** The steps a lens's legend lists, in display order, in the lens's unit. */
export function lensSteps(lens: Lens, t: LensThresholds): { band: LensBand; text: string }[] {
  switch (lens) {
    case "structure":
      return [];
    case "performance":
      return [
        { band: "green", text: "Great / on track" },
        { band: "amber", text: "Average" },
        { band: "red", text: "Low" },
        { band: "none", text: "Awaiting data" },
      ];
    case "finance":
      return [
        { band: "green", text: `under ${t.finance.near} % used` },
        { band: "amber", text: `${t.finance.near}–${t.finance.over} %` },
        { band: "red", text: `over ${t.finance.over} %` },
      ];
    case "completeness":
      return [
        { band: "green", text: "complete" },
        { band: "amber", text: "≥ 80%" },
        { band: "red", text: "below" },
      ];
    default: {
      const s = t[lens];
      return [
        { band: "green", text: `≥ ${s.green}${LENS_UNITS[lens]}` },
        { band: "amber", text: `≥ ${s.amber}${LENS_UNITS[lens]}` },
        { band: "red", text: "below" },
      ];
    }
  }
}

/** "Shown to the reporting line": who sees a lens the viewer may not. */
export function lensAudience(lens: Lens, visibility: Record<GatedDimension, Level>): Level | null {
  if (lens === "completeness") return "reporting_line";
  const gate = LENS_GATE[lens];
  return gate ? visibility[gate] : null;
}

// The viewer's lens, per company, in this browser --------------------------------------------

const LENS_KEY = (companyId: string) => `companyos.chart.lens.${companyId}`;

export function readLensPref(companyId: string): Lens | null {
  try {
    const v = window.localStorage.getItem(LENS_KEY(companyId));
    return isLens(v) ? v : null;
  } catch {
    return null;
  }
}

export function writeLensPref(companyId: string, lens: Lens | null): void {
  try {
    if (lens === null) window.localStorage.removeItem(LENS_KEY(companyId));
    else window.localStorage.setItem(LENS_KEY(companyId), lens);
  } catch {}
}

/**
 * The lens to draw: the URL's (`?lens=`), else the viewer's remembered one, else the company's
 * default, else Structure — and never a lens the company does not serve or the viewer cannot
 * open (then Structure, the floor).
 */
export function effectiveLens(
  url: Lens | undefined,
  pref: Lens | null,
  companyDefault: Lens | undefined,
  served: readonly Lens[],
  locked: (lens: Lens) => boolean,
): Lens {
  const want = url ?? pref ?? companyDefault ?? "structure";
  return served.includes(want) && !locked(want) ? want : "structure";
}
